/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { DeclarationsApiResponse } from './types';

import { AxiosError } from 'axios';
import apiClient from '../../services/api-client';
import { RootState } from '../../store/rootReducer';
import { storageDb } from '../../services/localforage';

export const getDeclarations = createAsyncThunk<DeclarationsApiResponse, void>(
  'declarations/getDeclarations',
  async () => {
    const response = await apiClient.get<DeclarationsApiResponse, AxiosError>('/declarations');
    return response.data;
  },
);

export interface DeclarationsState {
  data: DeclarationsApiResponse | null;
  status: 'idle' | 'pending' | 'fulfilled' | 'rejected';
  error: AxiosError | null;
}

const initialState: DeclarationsState = {
  data: null,
  status: 'idle',
  error: null,
};

export const declarationsPersistConfig = {
  key: 'declarations',
  storage: storageDb,
  // serialize: false,
  blacklist: ['status', 'error'],
};

const DeclarationsSlice = createSlice({
  name: 'declarations',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getDeclarations.pending, (state) => {
        state.status = 'pending';
        state.error = null;
      })
      .addCase(getDeclarations.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        state.data = action.payload;
      })
      .addCase(getDeclarations.rejected, (state, action) => {
        state.status = 'rejected';
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        state.error = action.error;
      });
  },
});

export const selectDeclarations = (state: RootState) => state.declarations.data;
export const selectDeclarationsStatus = (state: RootState) => state.declarations.status;

export default DeclarationsSlice;
